/**
 * @file: PreInspectionManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import {
  getSummaryOfSiteInspections,
  isMediaStagedPending,
} from '@euler/app/flows/task/functions/task-detail/util';
import { CommonTaskStatus, InspectionCategory } from '@euler/model/enum';
import { PreInspection } from '@euler/model/task-detail';
import { dateFromValue } from '@euler/utils/datetime';
import { ITaskManager } from '../types';
import { InspectionManager } from './InspectionManager';

export class PreInspectionManager extends InspectionManager<PreInspection> {
  constructor(taskManager: ITaskManager) {
    super(InspectionCategory.Pre, taskManager);
  }

  async beginPreInspection(technicianId?: number) {
    const { versions, response } = await this.taskService.beginPreInspection(
      this.taskNo,
      technicianId,
    );

    this.produceWithVersions(
      versions,
      (_, { version, inspection, original }) => {
        if (original.version !== version) {
          inspection.flows = response;
          if (original.status === CommonTaskStatus.Pending) {
            inspection.status = CommonTaskStatus.InProgress;
          }
        }
      },
    );
  }

  async finish(params: { technicianId: number; technicianName: string }) {
    const siteInspections = [...this.siteInspections$.getValue().values()];
    const { versions, timestamp } = await this.taskService.finishInspections(
      this.taskNo,
      InspectionCategory.Pre,
      params,
    );
    this.produceWithVersions(versions, (_, { inspection }) => {
      for (const flow of inspection.flows) {
        flow.status = 'finished';
        flow.finishedAt = dateFromValue(timestamp);
        flow.resultSummary = getSummaryOfSiteInspections(
          siteInspections,
          undefined,
          x => x.flowId === flow.id,
        );
      }
      inspection.finishedAt = dateFromValue(timestamp);
      inspection.finishedBy = params.technicianName;
      inspection.status = CommonTaskStatus.Finished;
    });
  }

  async cancel() {
    const { versions } = await this.taskService.cancelInspections(
      this.taskNo,
      InspectionCategory.Pre,
    );
    this.produceWithVersions(versions, (_, { inspection }) => {
      inspection.flows = [];
      inspection.sites = [];
      inspection.customIssues = [];
      inspection.dashboardImgUrl = undefined;
      inspection.signatureImgUrl = undefined;
      inspection.finishedAt = undefined;
      inspection.finishedBy = undefined;
      inspection.status = CommonTaskStatus.Pending;
    });
  }

  canFinish() {
    let hasPendingMediaUpload = false;
    for (const { manager } of this.siteInspectionContextMap.values()) {
      if (manager.isCommitting) return false;
      manager.enumerate({
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        onMedia: media => {
          if (isMediaStagedPending(media)) {
            hasPendingMediaUpload = true;
            return false;
          }
          return true;
        },
      });
      if (hasPendingMediaUpload) {
        return false;
      }
    }
    return true;
  }
}
